/**
 * @file Merges a form's schema with a default schema, keeping the form's overrides.
 */
import keyBy from "lodash.keyby";

export type SchemaInput = {
  name: string;
  type?: string;
  schema?: SchemaInput[];
  [key: string]: unknown;
};

const TYPE_OBJECT = "object";

/**
 * Merges the default schema fields with the custom inputs, recursing into nested object fields.
 */
const extendSchema = (schema: SchemaInput[], inputs: SchemaInput[]): SchemaInput[] => {
  const inputKeys = inputs.map((input) => input.name);
  const schemaKeys = schema.map((input) => input.name);

  const extraKeys = schemaKeys.filter((key) => !inputKeys.includes(key));
  const formKeys = [...inputKeys, ...extraKeys];

  const schemaMap: Record<string, SchemaInput> = keyBy(schema, "name");
  const inputsMap: Record<string, SchemaInput> = keyBy(inputs, "name");

  const extendedSchema = formKeys.map((key) => {
    const customInput = inputsMap[key];
    const defaultInput = schemaMap[key];

    if (!defaultInput) {
      return { ...customInput };
    }

    const { type } = defaultInput;
    const isObject = type === TYPE_OBJECT;

    const shouldExtendNested = isObject && !!customInput;

    if (shouldExtendNested) {
      const { schema: defaultSchema } = defaultInput;
      const { schema: inputSchema = [] } = customInput;

      customInput.schema = extendSchema(defaultSchema, inputSchema);
    }

    return {
      ...defaultInput,
      ...customInput,
    };
  });

  return extendedSchema;
};

export default extendSchema;
